import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const flap = (dir) => ({
  animate: { rotateY: [0, dir * 68, 0] },
  transition: { duration: 0.55, repeat: Infinity, ease: "easeInOut" },
});

const Defs = () => (
  <defs>
    <linearGradient id="wingGrad" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stopColor="#F06BA8" />
      <stop offset="100%" stopColor="#F26D7D" />
    </linearGradient>
    <linearGradient id="wingGradR" x1="1" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor="#F06BA8" />
      <stop offset="100%" stopColor="#F26D7D" />
    </linearGradient>
  </defs>
);

const DUST_COLORS = ["#D4AF37", "#F06BA8", "#FDFBF7", "#F26D7D"];
const STAR_CLIP = "polygon(50% 0%, 63% 37%, 100% 50%, 63% 63%, 50% 100%, 37% 63%, 0% 50%, 37% 37%)";

export default function Butterfly() {
  const bodyRef = useRef(null);
  const [dust, setDust] = useState([]);

  useEffect(() => {
    const spawn = setInterval(() => {
      const el = bodyRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      const now = Date.now();
      const p = {
        id: `${now}-${Math.random()}`,
        born: now,
        x: r.left + r.width / 2 + (Math.random() * 30 - 15),
        y: r.top + r.height / 2 + (Math.random() * 20 - 4),
        size: 4 + Math.random() * 6,
        color: DUST_COLORS[Math.floor(Math.random() * DUST_COLORS.length)],
        driftX: Math.random() * 34 - 17,
      };
      setDust((d) => [...d.filter((q) => now - q.born < 1700), p]);
    }, 110);
    return () => clearInterval(spawn);
  }, []);

  return (
    <>
      <div data-testid="fairy-dust-layer" className="fixed inset-0 z-30 pointer-events-none" aria-hidden="true">
        {dust.map((p) => (
          <motion.span
            key={p.id}
            initial={{ opacity: 0.95, scale: 1, x: p.x, y: p.y, rotate: 0 }}
            animate={{ opacity: 0, scale: 0.1, x: p.x + p.driftX, y: p.y + 30, rotate: 120 }}
            transition={{ duration: 1.55, ease: "easeOut" }}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: p.size,
              height: p.size,
              background: p.color,
              clipPath: STAR_CLIP,
              filter: `drop-shadow(0 0 5px ${p.color})`,
            }}
          />
        ))}
      </div>
      <motion.div
        data-testid="animated-butterfly"
        className="fixed left-0 top-0 z-40 pointer-events-none"
        animate={{
          x: ["-8vw", "20vw", "58vw", "88vw", "60vw", "26vw", "-8vw"],
          y: ["76vh", "38vh", "62vh", "16vh", "46vh", "70vh", "76vh"],
        }}
        transition={{ duration: 56, repeat: Infinity, ease: "easeInOut" }}
      >
        <motion.div
          ref={bodyRef}
          animate={{ rotate: [-12, 8, -6, 14, -10, 6, -12] }}
          transition={{ duration: 56, repeat: Infinity, ease: "easeInOut" }}
          style={{ filter: "drop-shadow(0 8px 18px rgba(240,107,168,0.45))" }}
        >
          <div style={{ position: "relative", width: 72, height: 62, perspective: "220px" }}>
            <motion.div
              style={{ position: "absolute", inset: 0, transformOrigin: "50% 50%", willChange: "transform" }}
              {...flap(1)}
            >
              <svg width="72" height="62" viewBox="0 0 100 84" fill="none" style={{ position: "absolute", inset: 0 }}>
                <Defs />
                <path d="M50 36 C34 8 6 4 9 28 C11 44 32 44 50 40 Z" fill="url(#wingGrad)" />
                <path d="M50 44 C36 48 16 54 23 66 C30 76 44 58 50 48 Z" fill="url(#wingGrad)" opacity="0.85" />
                <path d="M46 38 C34 22 20 14 12 16" stroke="#FDFBF7" strokeWidth="1" opacity="0.5" fill="none" />
              </svg>
            </motion.div>
            <motion.div
              style={{ position: "absolute", inset: 0, transformOrigin: "50% 50%", willChange: "transform" }}
              {...flap(-1)}
            >
              <svg width="72" height="62" viewBox="0 0 100 84" fill="none" style={{ position: "absolute", inset: 0 }}>
                <Defs />
                <path d="M50 36 C66 8 94 4 91 28 C89 44 68 44 50 40 Z" fill="url(#wingGradR)" />
                <path d="M50 44 C64 48 84 54 77 66 C70 76 56 58 50 48 Z" fill="url(#wingGradR)" opacity="0.85" />
                <path d="M54 38 C66 22 80 14 88 16" stroke="#FDFBF7" strokeWidth="1" opacity="0.5" fill="none" />
              </svg>
            </motion.div>
            <svg width="72" height="62" viewBox="0 0 100 84" fill="none" style={{ position: "absolute", inset: 0 }}>
              <ellipse cx="50" cy="44" rx="2.6" ry="16" fill="#2D2833" />
              <circle cx="50" cy="27" r="3.4" fill="#2D2833" />
              <path d="M48 25 C44 15 38 11 34 9" stroke="#D4AF37" strokeWidth="1.4" fill="none" strokeLinecap="round" />
              <path d="M52 25 C56 15 62 11 66 9" stroke="#D4AF37" strokeWidth="1.4" fill="none" strokeLinecap="round" />
            </svg>
          </div>
        </motion.div>
      </motion.div>
    </>
  );
}
